// 오너 교시 시간표 설정 페이지

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const API_BASE_URL = '';

interface PeriodTemplate {
  id?: string;
  period_number: number;
  period_name: string;
  period_type: string; // study, break, self_study
  start_time: string;
  end_time: string;
  display_order: number;
  is_active: boolean;
}

export default function PeriodTemplateSettingsPage() {
  const [templates, setTemplates] = useState<PeriodTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  // 스케줄 유형 state
  const [scheduleType, setScheduleType] = useState<'weekly' | 'period'>('weekly');
  const [isChangingType, setIsChangingType] = useState(false);
  const [bulkDays, setBulkDays] = useState<number[]>([0, 1, 2, 3, 4]);
  const [bulkPeriods, setBulkPeriods] = useState<number[] | null>(null);
  // 위험 액션 보호 — "적용" 텍스트 입력 확인 + 아코디언 펼침
  const [bulkConfirmText, setBulkConfirmText] = useState('');
  const [bulkExpanded, setBulkExpanded] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{open: boolean; newType: string}>({open: false, newType: ''});

  useEffect(() => {
    loadTemplates();
    loadScheduleType();
  }, []);

  const loadScheduleType = async () => {
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const orgId = localStorage.getItem('organization_id');
      if (!orgId) return;

      // schedule_type 전용 API 호출 (Raw SQL로 직접 조회)
      const response = await fetch(`${API_BASE_URL}/api/v1/owner/organization/${orgId}/schedule-type`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setScheduleType(data.schedule_type || 'weekly');
      }
    } catch (err) {
      console.error('스케줄 유형 로드 실패:', err);
    }
  };

  const handleScheduleTypeChange = (newType: string) => {
    if (newType === scheduleType) return;
    setConfirmDialog({ open: true, newType });
  };

  const confirmScheduleTypeChange = async () => {
    const newType = confirmDialog.newType;
    setConfirmDialog({ open: false, newType: '' });
    setIsChangingType(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/owner/organization/schedule-type`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ schedule_type: newType }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || '스케줄 유형 변경 실패');
      }

      setScheduleType(newType as 'weekly' | 'period');
      setSuccess(`스케줄 유형이 '${newType === 'period' ? '교시 스케줄' : '주간 스케줄'}'로 변경되었습니다.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsChangingType(false);
    }
  };

  const loadTemplates = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/owner/period-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('교시 시간표를 불러올 수 없습니다.');

      const data = await response.json();
      setTemplates(data.data || []);
      setIsDefault(data.is_default || false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (index: number, field: keyof PeriodTemplate, value: any) => {
    setTemplates((prev) =>
      prev.map((t, i) => (i === index ? { ...t, [field]: value } : t))
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/owner/period-templates/bulk`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          templates: templates.map((t) => ({
            period_number: t.period_number,
            period_name: t.period_name,
            period_type: t.period_type,
            start_time: t.start_time,
            end_time: t.end_time,
            display_order: t.display_order,
            is_active: t.is_active,
          })),
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || '저장 실패');
      }

      setSuccess('교시 시간표가 저장되었습니다.');
      setIsDefault(false);
      setTimeout(() => setSuccess(''), 3000);
      await loadTemplates();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'study': return 'primary';
      case 'break': return 'warning';
      case 'self_study': return 'secondary';
      default: return 'default';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'study': return '수업';
      case 'break': return '휴식';
      case 'self_study': return '자습';
      default: return type;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <AccessTimeIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          교시 시간표 설정
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        독서실의 교시별 시간을 설정합니다. 학생/학부모가 교시 스케줄을 등록할 때 이 시간표가 사용됩니다.
      </Typography>

      {/* 현재 스케줄 유형 표시 박스 숨김 (2026-07-02) — 교시 스케줄 단일 운영이라
          유형 선택 UI(라디오 1개) 불필요. schedule_type 로직/상태는 그대로 유지.
          되살리려면 이 블록을 복원하면 됨. handleScheduleTypeChange/confirmDialog 도 존치. */}

      {/* 스케줄 유형 변경 확인 다이얼로그 */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({open: false, newType: ''})}>
        <DialogTitle>스케줄 유형 변경</DialogTitle>
        <DialogContent>
          <DialogContentText>
            스케줄 유형을 '{confirmDialog.newType === 'period' ? '교시 스케줄' : '주간 스케줄'}'로 변경하시겠습니까?
            <br /><br />
            변경해도 기존 데이터는 삭제되지 않으며, 언제든지 다시 전환할 수 있습니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({open: false, newType: ''})}>취소</Button>
          <Button onClick={confirmScheduleTypeChange} variant="contained" autoFocus>변경</Button>
        </DialogActions>
      </Dialog>

      {isDefault && (
        <Alert severity="info" sx={{ mb: 2 }}>
          기본 시간표입니다. 독서실 운영 시간에 맞게 수정 후 저장해주세요.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, width: 120 }}>교시</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 80 }}>타입</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 130 }}>시작 시간</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 130 }}>종료 시간</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 80 }} align="center">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span>활성</span>
                  <Switch
                    size="small"
                    checked={templates.length > 0 && templates.every((t) => t.is_active)}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setTemplates((prev) => prev.map((t) => ({ ...t, is_active: val })));
                    }}
                    sx={{ mt: -0.5, mb: -0.5 }}
                  />
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template, index) => (
              <TableRow
                key={index}
                sx={{
                  backgroundColor: template.period_type === 'break' ? '#fff8e1' : 'inherit',
                  opacity: template.is_active ? 1 : 0.5,
                }}
              >
                <TableCell>
                  <TextField
                    size="small"
                    value={template.period_name}
                    onChange={(e) => handleChange(index, 'period_name', e.target.value)}
                    variant="standard"
                    sx={{ width: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={getTypeLabel(template.period_type)}
                    size="small"
                    color={getTypeColor(template.period_type) as any}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="time"
                    size="small"
                    value={template.start_time}
                    onChange={(e) => handleChange(index, 'start_time', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="time"
                    size="small"
                    value={template.end_time}
                    onChange={(e) => handleChange(index, 'end_time', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={template.is_active}
                    onChange={(e) => handleChange(index, 'is_active', e.target.checked)}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<RestoreIcon />}
          onClick={loadTemplates}
        >
          되돌리기
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isSaving}
          size="large"
        >
          {isSaving ? '저장 중...' : '시간표 저장'}
        </Button>
      </Box>

      {/* ⚠️ 위험 영역 — 페이지 최하단 + 기본 접힘 + "적용" 텍스트 확인 */}
      {scheduleType === 'period' && (() => {
        const DAY_NAMES = ['월', '화', '수', '목', '금', '토', '일'];
        const studyPeriods = (templates || []).filter((t: any) => t.period_type === 'study' && t.is_active !== false).sort((a: any, b: any) => a.period_number - b.period_number);
        const canApply = bulkConfirmText.trim() === '적용';

        return (
          <Accordion
            expanded={bulkExpanded}
            onChange={(_, isExpanded) => {
              setBulkExpanded(isExpanded);
              if (!isExpanded) setBulkConfirmText(''); // 닫히면 확인 텍스트 초기화
            }}
            sx={{ mt: 6, border: '1px dashed #d97706', backgroundColor: '#fffbeb', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmberIcon sx={{ color: '#d97706' }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#92400e' }}>
                  전체 학생 교시 일괄 적용 (위험)
                </Typography>
                <Chip
                  label="모든 학생 영향"
                  size="small"
                  sx={{ ml: 1, bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="warning" sx={{ mb: 2 }}>
                선택한 요일/교시가 <strong>이 조직의 모든 학생</strong>에게 즉시 적용됩니다.
                되돌릴 수 없으니 신중히 진행하세요.
              </Alert>

              {/* 요일 선택 */}
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>요일 선택</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {DAY_NAMES.map((name, idx) => (
                  <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: '1px solid #ccc', cursor: 'pointer', backgroundColor: (bulkDays || [0,1,2,3,4]).includes(idx) ? '#e8f5e9' : '#fff' }}>
                    <input type="checkbox" checked={(bulkDays || [0,1,2,3,4]).includes(idx)} onChange={(e) => {
                      const current = bulkDays || [0,1,2,3,4];
                      setBulkDays(e.target.checked ? [...current, idx].sort() : current.filter((d: number) => d !== idx));
                    }} />
                    {name}
                  </label>
                ))}
              </Box>

              {/* 교시 선택 */}
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>교시 선택</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {studyPeriods.map((p: any) => (
                  <label key={p.period_number} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: '1px solid #ccc', cursor: 'pointer', backgroundColor: (bulkPeriods || studyPeriods.map((s: any) => s.period_number)).includes(p.period_number) ? '#e3f2fd' : '#fff' }}>
                    <input type="checkbox" checked={(bulkPeriods || studyPeriods.map((s: any) => s.period_number)).includes(p.period_number)} onChange={(e) => {
                      const defaults = studyPeriods.map((s: any) => s.period_number);
                      const current = bulkPeriods || defaults;
                      setBulkPeriods(e.target.checked ? [...current, p.period_number].sort((a: number, b: number) => a - b) : current.filter((n: number) => n !== p.period_number));
                    }} />
                    {p.period_name} ({p.start_time}~{p.end_time})
                  </label>
                ))}
              </Box>

              {/* 확인 텍스트 입력 */}
              <Box sx={{ mt: 3, p: 2, bgcolor: '#fff', borderRadius: 1, border: '1px solid #fcd34d' }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  적용을 진행하려면 아래 칸에 <span style={{ color: '#dc2626' }}>적용</span> 을 입력하세요
                </Typography>
                <TextField
                  size="small"
                  value={bulkConfirmText}
                  onChange={(e) => setBulkConfirmText(e.target.value)}
                  placeholder="적용"
                  fullWidth
                  autoComplete="off"
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  color="warning"
                  disabled={!canApply}
                  onClick={async () => {
                    const days = bulkDays || [0,1,2,3,4];
                    const periods = bulkPeriods || studyPeriods.map((s: any) => s.period_number);
                    if (days.length === 0 || periods.length === 0) { alert('요일과 교시를 선택해주세요'); return; }
                    try {
                      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
                      const res = await fetch(`${API_BASE_URL}/v1/owner/period-templates/apply-all`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ days_of_week: days, period_numbers: periods }),
                      });
                      if (res.ok) {
                        const data = await res.json();
                        alert(data.message || '일괄 적용 완료');
                        setBulkConfirmText('');
                        setBulkExpanded(false);
                      } else {
                        const err = await res.json();
                        alert(`실패: ${err.detail || '알 수 없는 오류'}`);
                      }
                    } catch (e) {
                      alert('일괄 적용 중 오류 발생');
                    }
                  }}
                  sx={{ fontWeight: 700 }}
                >
                  {canApply ? '전체 학생 일괄 적용 실행' : '"적용" 입력 필요'}
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })()}
    </Box>
  );
}
